<template>
  <div dir="rtl" lang="ar" class="app-root" :class="{ 'fixed-layout': route.path.startsWith('/videos') }">
    <GlobalToast />
    <nav-page v-if="showLayout"></nav-page>
    <main class="page-main" :class="{ 'no-overflow': route.path.startsWith('/videos') }">
      <router-view v-slot="{ Component }">
        <transition name="page-fade" mode="out-in" appear>
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
    <footer-page v-if="showFooter"></footer-page>
  </div>
</template>

<script setup>
import { onMounted, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import NavPage from "@/components/NavPage.vue";
import FooterPage from "@/components/FooterPage.vue";
import GlobalToast from "@/components/GlobalToast.vue";
import { useTheme } from "@/composables/useTheme";

import { initSocket, disconnectSocket } from "@/services/socketService";
import { requestNotificationPermission, onForegroundMessage } from "@/firebaseConfig";

const route = useRoute();
const store = useStore();
const { initTheme, isDarkMode } = useTheme();

const isAuthenticated = computed(() => store.getters['auth/isAuthenticated']);
const currentUser = computed(() => store.state.auth.user);

const showLayout = computed(() => {
  const isDashboard = route.path.startsWith('/admin') || route.path.startsWith('/dashboard');
  const isMaintenance = route.name === 'Maintenance';
  const isQuranReader = route.path.startsWith('/read-quran');
  return !isDashboard && !isMaintenance && !isQuranReader;
});

const showFooter = computed(() => {
  return showLayout.value && !route.path.startsWith('/videos');
});



const loadAds = () => {
  setTimeout(() => {
    // كود Social Bar المعتمد لتصنيف Books
    const script1 = document.createElement('script');
    script1.src = 'https://pl28967121.profitablecpmratenetwork.com/7f/e5/19/7fe51927dcc92238a951db7a717f17ec.js';
    script1.async = true;
    document.body.appendChild(script1);
  }, 5000); 
};

onMounted(() => {
  store.dispatch('settings/fetchPublicSettings');
  initTheme();
  loadAds();
  
  if (isAuthenticated.value) {
    initSocket();
    requestNotificationPermission(currentUser.value?.id);
    onForegroundMessage();
    store.dispatch('notifications/fetchNotifications');
  }
});



// Watch for authentication changes to init/disconnect socket
watch(isAuthenticated, (newVal) => {
  if (newVal) {
    initSocket();
    requestNotificationPermission(currentUser.value?.id);
    store.dispatch('notifications/fetchNotifications');
  } else {
    disconnectSocket();
  }
});

// Watch for route changes to lock body scroll for videos page
watch(() => route.path, (newPath) => {
  if (newPath.startsWith('/videos')) {
    document.body.classList.add('body-scroll-lock');
    document.documentElement.classList.add('body-scroll-lock');
  } else {
    document.body.classList.remove('body-scroll-lock');
    document.documentElement.classList.remove('body-scroll-lock');
  }
}, { immediate: true });
</script>

<style>
/* 🌟 Beautiful Page Entry Animations - Premium Glassmorphism Style */
.page-fade-enter-active,
.page-fade-leave-active {
  transition: all 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.page-fade-enter-from {
  opacity: 0;
  transform: translateY(25px) scale(0.98);
  filter: blur(8px);
}

.page-fade-leave-to {
  opacity: 0;
  transform: translateY(-15px) scale(1.02);
  filter: blur(4px);
}

/* Global Utility for Entrance - Staggered beautiful fade */
.animate-entrance {
  animation: entranceFadeSlide 1s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
  opacity: 0; /* Starts hidden */
}

@keyframes entranceFadeSlide {
  0% {
    opacity: 0;
    transform: translateY(30px);
    filter: blur(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
}

/* Adding staggered delay classes for child elements */
.animate-delay-1 { animation-delay: 0.1s; }
.animate-delay-2 { animation-delay: 0.2s; }
.animate-delay-3 { animation-delay: 0.3s; }
.animate-delay-4 { animation-delay: 0.4s; }
.animate-delay-5 { animation-delay: 0.5s; }

.app-root {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-root.fixed-layout {
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  position: fixed; /* Lock to top to avoid browser UI shifts */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

/* Global scroll lock for the entire document */
.body-scroll-lock {
  overflow: hidden !important;
  height: 100% !important;
  width: 100% !important;
  position: fixed;
}

.page-main {
  flex: 1;
}

.no-overflow {
  flex: 1;
  height: calc(100vh - 84px); /* Fallback for navbar height */
  height: calc(100dvh - 84px);
  overflow: hidden;
  position: relative;
}

@media (max-width: 991px) {
  .no-overflow {
    height: calc(100dvh - 65px); /* Mobile navbar height estimate */
  }
}
</style>
